<div class="modal-header">
    <h5 class="modal-title">
        <i class="bi bi-shield-lock me-2"></i>
        {{ 'OAuth.modalTitle' | translate }} - {{ domainName }}
    </h5>
    <button type="button" class="btn-close" (click)="cancel()"></button>
</div>

<div class="modal-body">
    <!-- Loading Spinner -->
    <div *ngIf="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ 'General.loading' | translate }}</span>
        </div>
        <p class="mt-2 text-muted">{{ 'OAuth.loadingConfig' | translate }}</p>
    </div>

    <!-- Form -->
    <form [formGroup]="oauthForm" *ngIf="!loading">
        <!-- Help Alert -->
        <div class="alert alert-info">
            <i class="bi bi-info-circle me-2"></i>
            {{ 'OAuth.helpText' | translate }}
        </div>

        <!-- Azure AD Configuration Section -->
        <fieldset class="form-section mb-4">
            <legend class="form-section-legend">{{ 'OAuth.azureADConfig' | translate }}</legend>
            <div class="row g-3">
                <!-- Integration Name -->
                <div class="col-12">
                    <label class="form-label" [attr.for]="'integrationName'">
                        {{ 'OAuth.integrationName' | translate }}
                        <span class="text-danger">*</span>
                    </label>
                    <input 
                        type="text" 
                        class="form-control"
                        formControlName="integrationName"
                        [placeholder]="'OAuth.integrationNamePlaceholder' | translate">
                    <small class="form-text text-muted">{{ 'OAuth.integrationNameHelp' | translate }}</small>
                </div>

                <!-- Azure Tenant ID -->
                <div class="col-md-6">
                    <label class="form-label" [attr.for]="'azureTenantId'">
                        {{ 'OAuth.azureTenantId' | translate }}
                        <span class="text-danger">*</span>
                    </label>
                    <input 
                        type="text" 
                        class="form-control font-monospace"
                        formControlName="azureTenantId"
                        [placeholder]="'OAuth.azureTenantIdPlaceholder' | translate">
                    <small class="form-text text-muted">{{ 'OAuth.azureTenantIdHelp' | translate }}</small>
                </div>

                <!-- Azure Client ID -->
                <div class="col-md-6">
                    <label class="form-label" [attr.for]="'azureClientId'">
                        {{ 'OAuth.azureClientId' | translate }}
                        <span class="text-danger">*</span>
                    </label>
                    <input 
                        type="text" 
                        class="form-control font-monospace"
                        formControlName="azureClientId"
                        [placeholder]="'OAuth.azureClientIdPlaceholder' | translate">
                    <small class="form-text text-muted">{{ 'OAuth.azureClientIdHelp' | translate }}</small>
                </div>
            </div>
        </fieldset>

        <!-- Database Configuration Section -->
        <fieldset class="form-section mb-4">
            <legend class="form-section-legend">{{ 'OAuth.databaseConfig' | translate }}</legend>
            <div class="row g-3">
                <!-- Database Name -->
                <div class="col-12">
                    <label class="form-label" [attr.for]="'domainDB'">
                        {{ 'OAuth.domainDB' | translate }}
                        <span class="text-danger">*</span>
                    </label>
                    <input 
                        type="text" 
                        class="form-control font-monospace"
                        formControlName="domainDB"
                        [placeholder]="'OAuth.domainDBPlaceholder' | translate">
                </div>

                <!-- Database User (Optional) -->
                <div class="col-md-6">
                    <label class="form-label" [attr.for]="'domainDBUser'">
                        {{ 'OAuth.domainDBUser' | translate }}
                    </label>
                    <input 
                        type="text" 
                        class="form-control"
                        formControlName="domainDBUser"
                        [placeholder]="'OAuth.domainDBUserPlaceholder' | translate">
                </div>

                <!-- Database Password (Optional) -->
                <div class="col-md-6">
                    <label class="form-label" [attr.for]="'domainDBPassword'">
                        {{ 'OAuth.domainDBPassword' | translate }}
                    </label>
                    <input 
                        type="password" 
                        class="form-control font-monospace"
                        formControlName="domainDBPassword"
                        [placeholder]="'OAuth.domainDBPasswordPlaceholder' | translate">
                </div>
            </div>
        </fieldset>

        <!-- Description -->
        <div class="mb-3">
            <label class="form-label" [attr.for]="'description'">
                {{ 'OAuth.description' | translate }}
            </label>
            <textarea 
                class="form-control"
                formControlName="description"
                rows="3"
                [placeholder]="'OAuth.descriptionPlaceholder' | translate"></textarea>
        </div>

        <!-- Test Result Display -->
        <div *ngIf="testResult" class="alert" 
             [ngClass]="{'alert-success': testResult.overallSuccess, 'alert-warning': !testResult.overallSuccess}">
            <h6 class="alert-heading">
                <i class="bi" [ngClass]="testResult.overallSuccess ? 'bi-check-circle' : 'bi-exclamation-triangle'"></i>
                {{ 'OAuth.testResultTitle' | translate }}
            </h6>
            
            <!-- Azure AD Test Result -->
            <div class="d-flex align-items-center mb-2">
                <i class="bi me-2" 
                   [ngClass]="testResult.azureAuthSuccess ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'"></i>
                <strong>Azure AD:</strong>
                <span class="ms-2">{{ testResult.azureAuthMessage }}</span>
            </div>
            
            <!-- Database Test Result -->
            <div class="d-flex align-items-center">
                <i class="bi me-2" 
                   [ngClass]="testResult.databaseConnectionSuccess ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'"></i>
                <strong>Database:</strong>
                <span class="ms-2">{{ testResult.databaseConnectionMessage }}</span>
            </div>
        </div>
    </form>
</div>

<div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="cancel()" [disabled]="saving || testing">
        {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-outline-primary" (click)="testConnection()" 
            [disabled]="oauthForm.invalid || saving || testing">
        <span *ngIf="testing" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!testing" class="bi bi-lightning me-2"></i>
        {{ 'OAuth.testConnection' | translate }}
    </button>
    <button type="button" class="btn btn-primary" (click)="saveConfig()" 
            [disabled]="oauthForm.invalid || saving || testing">
        <span *ngIf="saving" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!saving" class="bi bi-save me-2"></i>
        {{ 'General.save' | translate }}
    </button>
</div>
